import axios from 'axios'
import { resolveApiBaseUrl } from '../utils/apiBase'
import { describeIdentifyImageError, isEmptyIdentification } from './identifyImageError'
import { sessionController } from './sessionRefresh'

export { sessionController }

const API_BASE_URL = resolveApiBaseUrl()

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`, // Ajouter /api car toutes les routes backend commencent par /api
  headers: {
    'Content-Type': 'application/json',
  },
})

sessionController.attachTo(api, { apiBaseUrl: API_BASE_URL })

export const authService = {
  login: (email, password, rememberMe = false) =>
    api.post('/auth/login', { email, password, remember_me: rememberMe }).then(res => res.data),
  
  signup: (email, password) =>
    api.post('/auth/signup', { email, password }).then(res => res.data),
  
  getCurrentUser: () => api.get('/auth/me').then(res => res.data),
  
  updatePassword: (currentPassword, newPassword) =>
    api.post('/auth/update-password', { current_password: currentPassword, new_password: newPassword }).then(res => res.data),
  
  refreshToken: () => sessionController.refreshSession({ force: true }),
}

export const cartsService = {
  list: () => api.get('/carts').then(res => res.data),
  create: (name, budget) => api.post('/carts', { name, budget }).then(res => res.data),
  update: (cartId, data) => api.put(`/carts/${cartId}`, data).then(res => res.data),
  delete: (cartId) => api.delete(`/carts/${cartId}`).then(res => res.data),
  duplicate: (cartId, payload) => api.post(`/carts/${cartId}/duplicate`, payload || {}).then(res => res.data),
}

export const itemsService = {
  signedMedia: (key) => api.get('/media', { params: { key, as_json: true } }).then((res) => res.data?.url || ''),
  list: (cartId, config) => api.get(`/carts/${cartId}/items`, config).then(res => res.data),
  listAll: () => api.get('/items').then(res => res.data),
  create: (cartId, item) => api.post(`/carts/${cartId}/items`, item).then(res => res.data),
  update: (itemId, item) => api.put(`/items/${itemId}`, item).then(res => res.data),
  delete: (itemId) => api.delete(`/items/${itemId}`).then(res => res.data),
  moveToSaved: (itemId) => api.post(`/items/${itemId}/move-to-saved`).then(res => res.data),
  refreshPrice: (itemId, html = null, productInfo = null, _skipWhitelist = false, forceScraperAPI = false) => {
    const body = productInfo 
      ? { product_info: productInfo, use_client_scraping: true, skip_whitelist: false, force_scraperapi: forceScraperAPI }
      : html 
        ? { html, use_client_scraping: true, skip_whitelist: false, force_scraperapi: forceScraperAPI }
        : { skip_whitelist: false, force_scraperapi: forceScraperAPI }
    console.log('[API] refreshPrice appelé avec forceScraperAPI:', forceScraperAPI, 'body:', JSON.stringify(body).substring(0, 200))
    return api.post(`/items/${itemId}/refresh-price`, body).then(res => res.data)
  },
  refreshAllPrices: (cartId) => api.post('/items/refresh-all-prices', null, {
    params: cartId ? { cart_id: cartId } : {},
    timeout: 600000 // 10 minutes de timeout pour permettre la mise à jour de nombreux items
  }).then(res => res.data),
}

export const savedItemsService = {
  list: () => api.get('/saved-items').then(res => res.data),
  create: (item) => api.post('/saved-items', item).then(res => res.data),
  update: (savedId, item) => api.put(`/saved-items/${savedId}`, item).then(res => res.data),
  delete: (savedId) => api.delete(`/saved-items/${savedId}`).then(res => res.data),
  moveToCart: (savedId, cartId) =>
    api.post(`/saved-items/${savedId}/move-to-cart/${cartId}`).then(res => res.data),
}

export const searchService = {
  global: (query) => api.get('/search', { params: { query } }).then(res => res.data),
  
  // Recherche sémantique
  semantic: (query, threshold = 0.5, limit = 20, signal) => {
    const options = threshold && typeof threshold === 'object' ? threshold : { threshold, limit, signal }
    return api.get('/search/semantic', {
      params: {
        query,
        threshold: options.threshold ?? 0.5,
        limit: options.limit ?? 20,
      },
      signal: options.signal,
    }).then((res) => res.data)
  },
  
  // Générer tous les embeddings
  generateAllEmbeddings: () => 
    api.post('/items/generate-all-embeddings').then(res => res.data),
}

export const productService = {
  extractFromUrl: (url) => api.post('/products/extract-from-url', { url }).then(res => res.data),
  lookupBarcode: (barcode) => api.post('/products/lookup-barcode', { barcode }).then(res => res.data),
  identifyImage: (blob, barcode = '', supplementalBlob = null, context = {}, request = {}) => {
    const form = new FormData()
    const imageFile = blob instanceof File
      ? blob
      : new File([blob], 'scan.jpg', { type: blob?.type || 'image/jpeg' })
    form.append('file', imageFile)
    if (barcode) form.append('barcode', barcode)
    if (supplementalBlob) {
      const extra = supplementalBlob instanceof File
        ? supplementalBlob
        : new File([supplementalBlob], 'scan-supplemental.jpg', {
          type: supplementalBlob?.type || 'image/jpeg',
        })
      form.append('supplemental_file', extra)
    }
    form.append('context', JSON.stringify(context || {}))
    return api.post('/products/identify-image', form, { signal: request.signal }).then((res) => {
      const data = res.data
      if (isEmptyIdentification(data) && data?.error) {
        const error = new Error(describeIdentifyImageError({
          response: { status: 502, data: { detail: data.error } },
        }))
        error.response = { status: 502, data: { detail: error.message, payload: data } }
        throw error
      }
      return data
    }).catch((error) => {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        throw error
      }
      const wrapped = new Error(describeIdentifyImageError(error))
      wrapped.response = error?.response
      wrapped.status = error?.response?.status
      throw wrapped
    })
  },
  confirmIdentification: (identification) =>
    api.post('/products/confirm-identification', {
      barcode: identification?.barcode || '',
      reference_key: identification?.reference_key || '',
      payload: identification || {},
    }).then((res) => res.data),
  cacheOfficialImage: (sourceUrl) =>
    api.post('/products/cache-official-image', { source_url: sourceUrl }).then((res) => res.data),
  searchOfficialImages: (identification, request = {}) =>
    api.post('/products/search-official-image', {
      name_fr: identification?.name_fr || identification?.name || '',
      name_en: identification?.name_en || identification?.nameEn || identification?.canonical_name_international || '',
      brand: identification?.brand || '',
      model_fr: identification?.model_fr || identification?.model || '',
      variant_fr: identification?.variant_fr || identification?.variant || '',
      category_fr: identification?.category_fr || identification?.category || '',
      generation: identification?.generation || '',
      color: identification?.color || '',
      commercial_ref: identification?.commercial_ref || '',
      barcode: identification?.barcode || '',
      analysis_id: identification?.analysis_id || '',
      search_queries: identification?.search_queries || identification?.searchQueries || [],
      identification_confidence: identification?.identification_confidence ?? identification?.confidence ?? 0,
      identification_status: identification?.identification_status || identification?.identificationStatus || '',
      link: identification?.link || '',
    }, { signal: request.signal }).then((res) => res.data),
  uploadScanImage: (blob, filename = 'scan-original.jpg') => {
    const form = new FormData()
    form.append('file', new File([blob], filename, { type: blob?.type || 'image/jpeg' }))
    return api.post('/upload/image', form).then((res) => res.data)
  },
}

export const placesService = {
  reverse: (lat, lng) => api.post('/places/reverse', { lat, lng }).then((res) => res.data),
  nearby: (lat, lng, query = '', radius = 120) =>
    api.post('/places/nearby', { lat, lng, query, radius }).then((res) => res.data),
}

export const exchangeRatesService = {
  jpyToEur: () => api.get('/exchange-rates/jpy-eur').then((res) => res.data),
}

export const priceHistoryService = {
  getHistory: (itemId, limitOrOptions = 500) => {
    if (!itemId) {
      return Promise.reject(new Error('Item ID is required'))
    }
    const options = typeof limitOrOptions === 'object' && limitOrOptions
      ? limitOrOptions
      : { limit: limitOrOptions }
    const limit = options.limit ?? 500
    const cleanItemId = String(itemId).trim()
    return api.get(`/items/${cleanItemId}/price-history`, {
      params: { limit },
      signal: options.signal,
    }).then((res) => res.data)
  },
}

// Service pour le réseau social
export const socialService = {
  // Posts
  getFeed: (page = 1, limit = 20) => 
    api.get('/social/posts/feed', { params: { page, limit } }).then(res => res.data),
  
  getPost: (postId) => 
    api.get(`/social/posts/${postId}`).then(res => res.data),
  
  createPost: (content, sharedCartId = null) => 
    api.post('/social/posts', { content, shared_cart_id: sharedCartId }).then(res => res.data),
  
  deletePost: (postId) => 
    api.delete(`/social/posts/${postId}`).then(res => res.data),
  
  likePost: (postId) => 
    api.post(`/social/posts/${postId}/like`).then(res => res.data),
  
  unlikePost: (postId) => 
    api.delete(`/social/posts/${postId}/like`).then(res => res.data),
  
  repostPost: (postId, content = null) => 
    api.post(`/social/posts/${postId}/repost`, content ? { content } : {}).then(res => res.data),
  
  unrepostPost: (postId) => 
    api.delete(`/social/posts/${postId}/repost`).then(res => res.data),
  
  // Commentaires
  getComments: (postId) => 
    api.get(`/social/posts/${postId}/comments`).then(res => res.data),
  
  createComment: (postId, content) => 
    api.post(`/social/posts/${postId}/comments`, { content }).then(res => res.data),
  
  deleteComment: (commentId) => 
    api.delete(`/social/comments/${commentId}`).then(res => res.data),
  
  // Amis
  getFriends: () => 
    api.get('/social/friends').then(res => res.data),
  
  getFriendRequests: () => 
    api.get('/social/friends/requests').then(res => res.data),
  
  sendFriendRequest: (userId) => 
    api.post('/social/friends/requests', { user_id: userId }).then(res => res.data),
  
  acceptFriendRequest: (requestId) => 
    api.post(`/social/friends/requests/${requestId}/accept`).then(res => res.data),
  
  rejectFriendRequest: (requestId) => 
    api.post(`/social/friends/requests/${requestId}/reject`).then(res => res.data),
  
  removeFriend: (userId) => 
    api.delete(`/social/friends/${userId}`).then(res => res.data),
  
  // Recherche d'utilisateurs
  searchUsers: (query) => 
    api.get('/social/users/search', { params: { query } }).then(res => res.data),
  
  getUserProfile: (userId) => 
    api.get(`/social/users/${userId}`).then(res => res.data),
  
  getUserPosts: (userId, page = 1, limit = 20) => 
    api.get(`/social/users/${userId}/posts`, { params: { page, limit } }).then(res => res.data),
  
  getUserLikedPosts: (userId, page = 1, limit = 20) => 
    api.get(`/social/users/${userId}/liked-posts`, { params: { page, limit } }).then(res => res.data),
  
  getUserFriends: (userId) => 
    api.get(`/social/users/${userId}/friends`).then(res => res.data),
  
  getSharedCartDetails: (cartId) => 
    api.get(`/social/shared-carts/${cartId}`).then(res => res.data),
  
  copySharedCartImage: (imageKey) =>
    api.post(`/social/shared-carts/copy-image?image_key=${encodeURIComponent(imageKey)}`).then(res => res.data),
  
  updateProfile: (userId, profileData) => 
    api.put(`/social/users/${userId}/profile`, profileData).then(res => res.data),
  
  followUser: (userId) => 
    api.post(`/social/users/${userId}/follow`).then(res => res.data),
  
  unfollowUser: (userId) => 
    api.delete(`/social/users/${userId}/follow`).then(res => res.data),
  
  // Messages privés
  getConversations: () => 
    api.get('/social/messages/conversations').then(res => res.data),
  
  getMessages: (userId, page = 1, limit = 50, before = undefined) =>
    api.get(`/social/messages/${userId}`, { params: { page, limit, before } }).then(res => res.data),
  
  sendMessage: (userId, content, sharedCartId = null) => 
    api.post('/social/messages', { user_id: userId, content, shared_cart_id: sharedCartId }).then(res => res.data),
  
  markAsRead: (userId) => 
    api.post(`/social/messages/${userId}/read`).then(res => res.data),
}

// Service pour les news Cam'it
export const adminService = {
  listUsers: (params) => api.get('/admin/users', { params }).then((res) => res.data),
  getUser: (userId) => api.get(`/admin/users/${userId}`).then((res) => res.data),
  listCarts: (userId) => api.get(`/admin/users/${userId}/carts`).then((res) => res.data),
  getCart: (cartId) => api.get(`/admin/carts/${cartId}`).then((res) => res.data),
  updateCart: (cartId, data) => api.patch(`/admin/carts/${cartId}`, data).then((res) => res.data),
  addItem: (cartId, data) => api.post(`/admin/carts/${cartId}/items`, data).then((res) => res.data),
  updateItem: (itemId, data) => api.patch(`/admin/items/${itemId}`, data).then((res) => res.data),
  deleteItem: (itemId) => api.delete(`/admin/items/${itemId}`).then((res) => res.data),
  resetPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`).then((res) => res.data),
  audit: (params) => api.get('/admin/audit', { params }).then((res) => res.data),
}

export const presenceService = {
  heartbeat: () => api.post('/presence/heartbeat').then((res) => res.data),
}

export const newsService = {
  getAll: () => 
    api.get('/news').then(res => res.data),
  
  create: (title, content) => 
    api.post('/news', { title, content }).then(res => res.data),
  
  delete: (newsId) => 
    api.delete(`/news/${newsId}`).then(res => res.data),
}

// Service pour l'optimisation budgétaire
export const budgetOptimizationService = {
  // Optimiser un panier avec l'IA
  optimizeBudget: (cartId, targetBudget, modelType = 'economical') =>
    api.post(`/carts/${cartId}/optimize-budget`, {
      target_budget: targetBudget,
      model_type: modelType // 'economical' (GPT-4o-mini) ou 'high_quality' (GPT-4o)
    }).then(res => res.data),

  translateExplanations: (cartId, result, modelType = 'economical', scenarioId = null) =>
    api.post(`/carts/${cartId}/optimize-budget/translate-explanations`, {
      model_type: modelType,
      result,
      scenario_id: scenarioId,
    }).then(res => res.data),

  translateSavedExplanations: (cartId, scenarioId, modelType = 'economical') =>
    api.post(`/carts/${cartId}/optimize-budget/translate-explanations`, {
      model_type: modelType,
      scenario_id: scenarioId,
    }).then(res => res.data),
  
  // Appliquer un scénario d'optimisation
  applyScenario: (cartId, scenarioId, selectedSuggestions = null) =>
    api.post(`/carts/${cartId}/apply-scenario`, {
      scenario_id: scenarioId,
      selected_suggestions: selectedSuggestions
    }).then(res => res.data),
  
  // Récupérer l'historique des optimisations
  getHistory: (cartId) =>
    api.get(`/carts/${cartId}/optimization-history`).then(res => res.data),
  
  // Envoyer un feedback sur une suggestion
  submitFeedback: (scenarioId, feedbackType, comment = null, suggestionId = null) =>
    api.post(`/optimization/${scenarioId}/feedback`, {
      feedback_type: feedbackType, // 'helpful', 'not_helpful', 'incorrect'
      comment,
      suggestion_id: suggestionId
    }).then(res => res.data),
}

// Service pour les liens de partage
export const sharedLinksService = {
  createShareLink: (cartId, expiresInDays = null, name = null) =>
    api.post(`/carts/${cartId}/share`, {
      cart_id: cartId,
      expires_in_days: expiresInDays,
      name,
    }).then(res => res.data),

  shareCart: (cartId, expiresInDays = null, name = null) =>
    sharedLinksService.createShareLink(cartId, expiresInDays, name),
  
  // Récupérer les détails d'un panier partagé (public, sans auth)
  getSharedCartDetails: (token) => 
    axios.get(`${API_BASE_URL}/api/shared/${token}`).then(res => res.data),
  
  // Supprimer un lien de partage
  deleteShareLink: (token) => 
    api.delete(`/shared/${token}`).then(res => res.data),
  
  // Lister tous les liens de partage pour un panier
  listShareLinks: (cartId) => 
    api.get(`/carts/${cartId}/shared-links`).then(res => res.data),
}

export default api





